import { Component, computed, effect, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import _ from 'lodash';
import { BreadcrumbService, EnumValues, ModalService } from '@card-software/library';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { AuthService } from '@card-software/nexus-utilities';
import { ProductionCardDyeingCycle } from '@app/models/dyeing-dashboard/production-card-dyeing-cycle';
import { InventoryTransaction } from '@app/models/knitting/kntiting-requistion-report';
import { Roll } from '@app/models/knitting/roll';
import { Fabric } from '@app/models/order/work-order';
import { FinishingRoute } from '@app/models/production/finishing-route';
import { ProductionCard, ProductionCardStatus, ProductionCardStatusOption } from '@app/models/production/production-card';
import { ProductionCardBundleEvent, ProductionCardBundleStatus } from '@app/models/production/production-card/production-card-bundle';
import { TransferReportOption } from '@app/models/production/production-card/production-card-transfer';
import { ProductionCardRoles } from '@app/models/roles/roles-group';
import { FabricProxyService } from '@app/proxies/item/fabric.proxy.service';
import { FinishingRouteProxyService } from '@app/proxies/production/finishing-route.proxy.service';
import { ProductionCardsProxyService } from '@app/proxies/production/production-card.proxy.service';
import { ProductionCardDyeingCycleProxyService } from '@app/proxies/production/production-card-dyein-cycle.proxy.service';
import { ReportProxyService } from '@app/proxies/production/report.proxy.service';
import { EnumService } from '@app/services/enum.service';
import { IndividualProductionCardService } from '@app/services/individual-production-card.service';
import { UserAuthenticationService } from '@app/services/user-authentication.service';
import { navigateCrossDomain } from '@app/utilities/cross-domain-navigation.util';
import { FinishingRouteUtil } from '@app/utilities/finishing-route.util';
import { BatchingCardViewMode, ProductionCardStatusUtil } from '@app/utilities/production-card-status.util';
import { filterAssignableStatuses } from '@app/utilities/production-card-status-role.util';
import { ChangeDyeingMachineModalComponent } from '../_modals/change-dyeing-machine-modal/change-dyeing-machine-modal.component';
import { CloseProductionCardModalComponent } from '../_modals/close-production-card-modal/close-production-card-modal.component';
import { ReassignmentModalComponent } from '../_modals/reassignment-modal/reassignment-modal.component';

@Component({
  selector: 'app-individual-production-card-details',
  templateUrl: './individual-production-card-details.component.html',
  styleUrl: './individual-production-card-details.component.scss',
})
export class IndividualProductionCardDetailsComponent implements OnInit, OnDestroy {
  productionCardStatusValues: EnumValues;
  fabricFormValues: EnumValues;
  fabricFormEnums: EnumValues;

  status: FormControl = new FormControl();

  productionCardStatus = ProductionCardStatus;
  productionCardBundleStatus = ProductionCardBundleStatus;

  finishingRoutes = signal<FinishingRoute[]>([]);
  dyeingCycles = signal<ProductionCardDyeingCycle[]>([]);
  isChangingDyeingMachine = signal<boolean>(false);
  canClose = signal<boolean>(false);
  isClosing = signal<boolean>(false);
  isMainViewerOnly =
    AuthService.hasRole(ProductionCardRoles.ProductionMainViewer) && !AuthService.hasRole(ProductionCardRoles.ProductionCardsEditor);

  get isProductionCardsEditor(): boolean {
    return AuthService.hasRole(ProductionCardRoles.ProductionCardsEditor);
  }

  // Cache for fabric data to avoid repeated HTTP calls
  private fabricsMap = signal<Map<string, Fabric>>(new Map());

  // Check if any finishing route has the Rolling process pending (will need transfer eventually)
  routesWithRollingPending = computed(() => {
    return this.finishingRoutes().filter((route) => FinishingRouteUtil.hasRollingProcessPending(route));
  });

  hasRoutesWithRollingPending = computed(() => this.routesWithRollingPending().length > 0);

  // Get route numbers that need transfer for display
  routeNumbersWithRollingPending = computed(() => {
    return this.routesWithRollingPending()
      .map((route) => route.number)
      .join(', ');
  });

  activeInventoryTransaction = computed<InventoryTransaction | null>(() => {
    const transactions = this.individualProductionCardService.activeInventoryTransactionsInBatching;
    return transactions.length > 0 ? transactions[0] : null;
  });

  rolls = computed<Roll[]>(() => {
    const pc = this.individualProductionCardService.productionCard();

    // For reprocess production cards at ReadyForFinishing, use all available rolls directly
    // (no batching transaction exists in this case)
    if (
      pc &&
      pc.reprocessNumber &&
      pc.reprocessNumber > 0 &&
      (pc.status === ProductionCardStatus.ReadyForFinishing ||
        pc.status === ProductionCardStatus.Brd ||
        pc.status === ProductionCardStatus.Finishing)
    ) {
      return this.individualProductionCardService.availableRolls;
    }

    const allLines = this.individualProductionCardService.activeInventoryTransactionsInBatching.flatMap((tx) => tx.lines || []);
    const transactionRollNumbers = allLines.map((d) => d.lotItemKey.number);
    const rolls = this.individualProductionCardService.availableRolls.filter((r) => transactionRollNumbers.includes(r.number!));

    return rolls.map((r) => ({
      ...r,
      overrideWeight: allLines.find((d) => d.lotItemKey.number === r.number)?.quantity,
    }));
  });

  // Transfer report options based on production card status
  availableTransferReports = computed<TransferReportOption[]>(() => {
    const card = this.individualProductionCardService.productionCard();
    if (!card?.id) return [];

    const options: TransferReportOption[] = [];

    // Greige → Batching - one entry per inventory transaction
    if (card.status >= ProductionCardStatus.Batching) {
      const transactions = this.individualProductionCardService.activeInventoryTransactionsInBatchingSignal();
      if (transactions.length === 0) {
        options.push({
          label: 'Greige → Batching',
          entityId: card.id,
          entityType: 'production-card',
          transferType: 'greige-to-batching',
        });
      } else {
        transactions.forEach((tx, idx) => {
          options.push({
            label: transactions.length > 1 ? `Greige → Batching (${idx + 1})` : 'Greige → Batching',
            entityId: tx.id,
            entityType: 'inventory-transaction',
            transferType: 'greige-to-batching',
          });
        });
      }
    }

    // Batching → Greige (return transactions) - one entry per return transaction
    if (card.status >= ProductionCardStatus.Batching) {
      const returnTxs = this.individualProductionCardService.inventoryTransactionsBatchingToGreigeSignal();
      returnTxs.forEach((tx, idx) => {
        options.push({
          label: returnTxs.length > 1 ? `Batching → Greige (${idx + 1})` : 'Batching → Greige',
          entityId: tx.id,
          entityType: 'inventory-transaction',
          transferType: 'batching-to-greige',
        });
      });
    }

    // Batching → BRD (status >= Brd)
    if (card.status >= ProductionCardStatus.Brd) {
      options.push({
        label: 'Batching → BRD',
        entityId: card.id,
        entityType: 'production-card',
        transferType: 'batching-to-brd',
      });
    }

    // BRD → Dyeing (status >= Dyeing) - One option per DyeingCycle
    if (card.status >= ProductionCardStatus.Dyeing) {
      const cycles = this.dyeingCycles();
      cycles.forEach((cycle) => {
        options.push({
          label: cycles.length > 1 ? `BRD → Dyeing (Cycle ${cycle.cycleNumber})` : 'BRD → Dyeing',
          entityId: cycle.id,
          entityType: 'dyeing-cycle',
          transferType: 'brd-to-dyeing',
        });
      });
    }

    // Dyeing → Finishing (status >= Finishing)
    if (card.status >= ProductionCardStatus.Finishing) {
      options.push({
        label: 'Dyeing → Finishing',
        entityId: card.id,
        entityType: 'production-card',
        transferType: 'dyeing-to-finishing',
      });
    }

    return options;
  });

  showTransferReportsButton = computed(() => this.availableTransferReports().length > 0);

  get productionCard(): ProductionCard | null {
    return this.individualProductionCardService.productionCard();
  }

  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly individualProductionCardService = inject(IndividualProductionCardService);
  private readonly dyeingCycleProxyService = inject(ProductionCardDyeingCycleProxyService);
  private readonly enumService = inject(EnumService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly userAuthenticationService = inject(UserAuthenticationService);
  private readonly finishingRouteProxyService = inject(FinishingRouteProxyService);
  private readonly reportProxyService = inject(ReportProxyService);
  private readonly translate = inject(TranslateService);
  private readonly messageService = inject(MessageService);
  private readonly modalService = inject(ModalService);
  private readonly fabricProxyService = inject(FabricProxyService);
  private readonly productionCardProxyService = inject(ProductionCardsProxyService);

  private breadcrumbService = inject(BreadcrumbService);

  constructor() {
    effect(
      () => {
        const card = this.productionCard;
        if (_.isNil(card)) return;

        const workOrder = this.individualProductionCardService.workOrder;
        this.breadcrumbService.set([
          { label: 'Planning' },
          {
            label: workOrder?.orderNumber ?? '',
            route: ['/production-cards-details'],
            queryParams: { id: workOrder?.id, workOrderId: workOrder?.id },
          },
          { label: card.productionCardNumber ?? '' },
        ]);
      },
      { allowSignalWrites: true },
    );
    this.productionCardStatusValues = this.enumService.getEnum('ProductionCardStatus', 'productionEnums')?.values || {};
    this.fabricFormValues = this.enumService.getEnum('FabricForm', 'itemEnums')?.values || {};
    this.fabricFormEnums = this.enumService.getEnum('FabricForm', 'itemEnums')?.values || {};

    if (AuthService.hasRole(ProductionCardRoles.ProductionCardsCloser)) {
      this.canClose.set(true);
    }

    this.individualProductionCardService.isInitialized$.subscribe(() => {
      this.getAvailableRolls();
      this.individualProductionCardService.proxyGetActiveInventoryTransactionsInBatching().subscribe(() => {
        this.getAvailableRolls();
      });
      this.individualProductionCardService.proxyGetInventoryTransactionsBatchingToGreige().subscribe();
      // Handle reprocess-specific roll loading
      this.individualProductionCardService.proxyGetRollsForReprocess().subscribe();
    });
  }

  ngOnInit() {
    this.route.params.subscribe((params) => {
      const productionCardNumber = params['productionCardNumber'];
      if (productionCardNumber) {
        this.individualProductionCardService.initialize(productionCardNumber).subscribe({
          next: (card) => {
            this.status.setValue(card.status, { emitEvent: false });
            this.loadDyeingCycles();
            this.loadFinishingRoutes();
          },
          error: (error) => {
            console.error('Failed to initialize production card:', error);
          },
        });
      }
    });

    this.status.valueChanges.subscribe((newStatus) => {
      this.changeStatus(newStatus);
    });
  }

  ngOnDestroy() {
    this.individualProductionCardService.reset();
    this.finishingRoutes.set([]);
    this.dyeingCycles.set([]);
    this.status.reset();
  }

  loadDyeingCycles() {
    const card = this.individualProductionCardService.productionCard();
    if (!card?.id) return;

    this.dyeingCycleProxyService.getByProductionCardId(card.id).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.dyeingCycles.set(response.data);
        }
      },
      error: (error) => {
        console.error('Failed to load dyeing cycles:', error);
      },
    });
  }

  loadFinishingRoutes() {
    this.individualProductionCardService.proxyGetFinishingRoutes().subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.finishingRoutes.set(response.data);
          this.loadFabricsForRoutes(response.data);
        }
      },
      error: (error) => {
        console.error('Failed to load finishing routes:', error);
      },
    });
  }

  private loadFabricsForRoutes(routes: FinishingRoute[]): void {
    // Collect all unique fabric codes from all routes
    const allFabricCodes = routes.flatMap((route) => route.fabricCodes || []);
    const uniqueFabricCodes = [...new Set(allFabricCodes)];

    if (uniqueFabricCodes.length === 0) return;

    this.fabricProxyService.batchFabrics(uniqueFabricCodes).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          const newMap = new Map<string, Fabric>();
          response.data.forEach((fabric) => {
            newMap.set(fabric.fabricCode, fabric);
          });
          this.fabricsMap.set(newMap);
        }
      },
      error: (error) => {
        console.error('Failed to load fabrics:', error);
      },
    });
  }

  getAvailableRolls() {
    return this.individualProductionCardService.proxyGetAvailableRolls().subscribe({
      next: () => {
        // Rolls are now available in individualProductionCardService.availableRolls
      },
      error: (error) => {
        console.error('Failed to fetch available rolls:', error);
      },
    });
  }

  goToBundles() {
    const card = this.individualProductionCardService.productionCard();
    if (card?.productionCardNumber) {
      this.router.navigate(['/production-card', card.productionCardNumber, 'bundles']);
    }
  }

  goToFinishingRoute(finishingRouteNumber: string) {
    const card = this.individualProductionCardService.productionCard();
    if (card?.productionCardNumber) {
      this.router.navigate(['/production-card', card.productionCardNumber, 'finishing-route', finishingRouteNumber]);
    }
  }

  isRouteCompleted(route: FinishingRoute): boolean {
    return route.route?.every((p) => p.endTime) ?? false;
  }

  isRoutePendingRolling(route: FinishingRoute): boolean {
    return (
      !this.isRouteCompleted(route) &&
      FinishingRouteUtil.areAllFinishingProcessesCompleted(route) &&
      FinishingRouteUtil.hasRollingProcessPending(route)
    );
  }

  getCompletedProcessesCount(route: FinishingRoute): number {
    return route.route?.filter((p) => p.endTime).length ?? 0;
  }

  getTotalProcessesCount(route: FinishingRoute): number {
    return route.route?.length ?? 0;
  }

  getRollsCount(route: FinishingRoute): number {
    return route.rollNumbers?.length ?? 0;
  }

  getFabric(route: FinishingRoute): string {
    const fabricCodes = route.fabricCodes || [];
    const map = this.fabricsMap();

    return fabricCodes
      .map((code) => map.get(code)?.longDescription)
      .filter((desc) => desc)
      .join(', ');
  }

  getFabricType(route: FinishingRoute): string {
    const fabricCodes = route.fabricCodes || [];
    const map = this.fabricsMap();

    return fabricCodes
      .map((code) => {
        const fabricForm = map.get(code)?.fabricForm;
        return fabricForm !== undefined && fabricForm !== null ? this.fabricFormValues[fabricForm] : null;
      })
      .filter((form) => form)
      .join(', ');
  }
  getFabricDisplay(route: FinishingRoute): string {
    const workOrder = this.individualProductionCardService.workOrder;
    if (!workOrder?.fabrics || !route.fabricCodes?.length) {
      return 'N/A';
    }

    return route.fabricCodes
      .map((fabricCode) => {
        const workOrderFabric = workOrder.fabrics.find((f) => f.fabricCode === fabricCode);
        if (!workOrderFabric) {
          return fabricCode;
        }
        const displayName = workOrderFabric.fabric?.fieldToDisplay || fabricCode;
        const fabricForm = workOrderFabric.fabric?.fabricForm;
        const fabricFormLabel = fabricForm !== undefined ? this.fabricFormEnums[fabricForm] || '' : '';
        return fabricFormLabel ? `${displayName} - ${fabricFormLabel}` : displayName;
      })
      .join(', ');
  }

  get dyeingCyclesStatus(): { label: string; severity: 'success' | 'info' } {
    const cycles = this.dyeingCycles();
    if (cycles.length === 0) {
      return { label: this.translate.instant('Individual_Production_Card_Details_NoCycles'), severity: 'info' };
    }

    const allCompleted = cycles.every((cycle) => cycle.endTime && cycle.removedTime);

    if (allCompleted) {
      return { label: this.translate.instant('Individual_Production_Card_Details_Completed'), severity: 'success' };
    }

    return { label: this.translate.instant('Individual_Production_Card_Details_InProcess'), severity: 'info' };
  }

  getColorStatusApproval(cycle: ProductionCardDyeingCycle): { label: string; severity: 'success' | 'danger' } {
    if (cycle.colorStatusApproval?.isApproved) {
      return { label: this.translate.instant('Individual_Production_Card_Details_Approved'), severity: 'success' };
    } else {
      return { label: this.translate.instant('Individual_Production_Card_Details_Rejected'), severity: 'danger' };
    }
  }

  goToCycleRecipeValues(cycleId: string): void {
    this.router.navigate(['/production/cycles', cycleId, 'recipe-values']);
  }

  get finishingRoutesStatus(): { label: string; severity: 'success' | 'info' } {
    const routes = this.finishingRoutes();
    if (routes.length === 0) {
      return { label: this.translate.instant('Individual_Production_Card_Details_NoRoutes'), severity: 'info' };
    }

    const allCompleted = routes.every((route) => this.isRouteCompleted(route));

    if (allCompleted) {
      return { label: this.translate.instant('Individual_Production_Card_Details_Completed'), severity: 'success' };
    }

    return { label: this.translate.instant('Individual_Production_Card_Details_InProcess'), severity: 'info' };
  }

  getCycleNumber(index: number): string {
    return `DM-${index + 1} Cycle Run`;
  }
  goToFinishedGoods() {
    const card = this.individualProductionCardService.productionCard();
    if (card?.productionCardNumber) {
      navigateCrossDomain(
        {
          domain: 'inventory',
          path: 'finished-good-transfers',
          queryParams: { productionCardNumber: card.productionCardNumber },
        },
        this.userAuthenticationService.getStoredJwtToken(),
      );
    }
  }

  get showFinishedGoodsCard(): boolean {
    if (_.isNil(this.productionCard)) return false;
    // Show finished goods card when status is Finishing or FinishedGood
    return (
      this.productionCard.status === ProductionCardStatus.Finishing || this.productionCard.status === ProductionCardStatus.FinishedGood
    );
  }

  /** Valid next statuses that the user's active-site roles allow them to assign. */
  private readonly assignableNextStatuses = computed<ProductionCardStatus[]>(() => {
    const card = this.individualProductionCardService.productionCard();
    if (_.isNil(card)) return [];
    return filterAssignableStatuses(ProductionCardStatusUtil.getValidNextStatuses(card.status), AuthService.listRoles());
  });

  showSelect = computed<boolean>(() => this.assignableNextStatuses().length > 0);

  selectOptions = computed<ProductionCardStatusOption[]>(() => {
    const card = this.individualProductionCardService.productionCard();
    if (_.isNil(card)) return [];
    return [
      { label: this.productionCardStatusValues[card.status], value: card.status, disabled: true },
      ...this.assignableNextStatuses().map((status) => ({
        label: this.productionCardStatusValues[status],
        value: status,
        disabled: false,
      })),
    ];
  });

  changeStatus(newStatus: number) {
    if (_.isNil(this.productionCard)) return;
    if (newStatus === this.productionCard.status) return;

    if (this.productionCard.status === ProductionCardStatus.Batching && newStatus === ProductionCardStatus.InRouteToBrd) {
      const weightedBundles = this.individualProductionCardService.productionCardBundles.filter(
        (b) => b.status === ProductionCardBundleStatus.Weighed,
      );
      const rollsInWeightedBundles = new Set(weightedBundles.flatMap((b) => b.rollNumbers));
      const pendingRolls = this.rolls().filter((r) => r.number && !rollsInWeightedBundles.has(r.number));

      if (pendingRolls.length > 0) {
        this.confirmationService.confirm({
          key: 'warning-dialog',
          message: this.translate.instant('Production_Card_Batching_PendingRollsWarning', { count: pendingRolls.length }),
          header: this.translate.instant('Production_Card_Batching_PendingRollsWarning_Header'),
          icon: 'pi pi-exclamation-triangle',
          acceptLabel: this.translate.instant('Production_Card_Batching_ProceedAnyway'),
          rejectLabel: this.translate.instant('Cancel'),
          accept: () => {
            this.executeStatusChange(newStatus);
          },
          reject: () => {
            this.status.setValue(this.productionCard!.status, { emitEvent: false });
          },
        });
        return;
      }
    }

    this.confirmationService.confirm({
      key: 'general-dialog',
      message: this.translate.instant('Production_Card_Change_Status_Confirm', { status: this.productionCardStatusValues[newStatus] }),
      acceptLabel: this.translate.instant('Yes'),
      rejectLabel: this.translate.instant('No'),
      accept: () => {
        this.executeStatusChange(newStatus);
      },
      reject: () => {
        this.status.setValue(this.productionCard!.status, { emitEvent: false });
      },
    });
  }

  //#endregion

  //#region

  get events(): ProductionCardBundleEvent[] {
    const productionCard = this.productionCard;
    if (_.isNil(productionCard)) return [];

    const events: ProductionCardBundleEvent[] = [];
    const hasBundles = this.individualProductionCardService.productionCardBundles.length > 0;

    // 1. Rolls Selected
    let status = 'standby';
    let color = 'transparent';

    if (productionCard.status >= ProductionCardStatus.InRouteToBatching) {
      status = 'completed';
      color = '#66BB6A'; // green for completed
    }

    events.push({
      key: 'rolls_selected',
      status: status,
      color: color,
      displayName: this.translate.instant('Individual_Production_Card_Details_RollsSelected'),
    });

    // 2. In Route to Batching
    status = 'standby';
    color = 'transparent';

    if (productionCard.status === ProductionCardStatus.InRouteToBatching) {
      status = 'inprogress';
      color = '#FFA726'; // yellow/orange for in progress
    } else if (productionCard.status > ProductionCardStatus.InRouteToBatching) {
      status = 'completed';
      color = '#66BB6A'; // green for completed
    }

    events.push({
      key: 'in_route_to_batching',
      status: status,
      color: color,
      displayName: this.translate.instant('Individual_Production_Card_Details_InRouteToBatching'),
    });

    // 3. In Batching
    status = 'standby';
    color = 'transparent';

    if (productionCard.status === ProductionCardStatus.Batching && !hasBundles) {
      status = 'inprogress';
      color = '#FFA726'; // yellow/orange for in progress
    } else if (productionCard.status >= ProductionCardStatus.Batching && hasBundles) {
      status = 'completed';
      color = '#66BB6A'; // green for completed
    }

    events.push({
      key: 'in_batching',
      status: status,
      color: color,
      displayName: this.translate.instant('Individual_Production_Card_Details_InBatching'),
    });

    // 4. Bundles Created
    status = 'standby';
    color = 'transparent';

    if (hasBundles) {
      status = 'completed';
      color = '#66BB6A'; // green for completed
    }

    events.push({
      key: 'bundles_created',
      status: status,
      color: color,
      displayName: this.translate.instant('Individual_Production_Card_Details_BundlesCreated'),
    });

    return events;
  }

  get batchingCardViewMode(): BatchingCardViewMode {
    if (_.isNil(this.productionCard)) return null;
    return ProductionCardStatusUtil.BatchingCardViewMode(
      this.individualProductionCardService.productionCardBundles,
      this.productionCard.status,
      this.productionCard.reprocessNumber,
    );
  }

  getBundles(status: ProductionCardBundleStatus): number {
    return this.individualProductionCardService.productionCardBundles.filter((b) => b.status === status).length;
  }

  get bundleStatusSummary(): {
    label: string;
    count: number;
    severity: 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast';
  }[] {
    const summary: {
      label: string;
      count: number;
      severity: 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast';
    }[] = [];

    const processing = this.getBundles(ProductionCardBundleStatus.Processing);
    if (processing > 0) {
      summary.push({
        label: this.translate.instant('Individual_Production_Card_Details_Processing'),
        count: processing,
        severity: 'info',
      });
    }

    const readyForWeighing = this.getBundles(ProductionCardBundleStatus.ReadyForWeighing);
    if (readyForWeighing > 0) {
      summary.push({
        label: this.translate.instant('Individual_Production_Card_Details_ReadyForWeighing'),
        count: readyForWeighing,
        severity: 'warn',
      });
    }

    const weighed = this.getBundles(ProductionCardBundleStatus.Weighed);
    if (weighed > 0) {
      summary.push({
        label: this.translate.instant('Individual_Production_Card_Details_Weighed'),
        count: weighed,
        severity: 'success',
      });
    }

    const brd = this.getBundles(ProductionCardBundleStatus.BRD);
    if (brd > 0) {
      summary.push({
        label: this.translate.instant('Individual_Production_Card_Details_BRD'),
        count: brd,
        severity: 'secondary',
      });
    }

    return summary;
  }
  //#endregion

  public printReport(finishingRoute: FinishingRoute): void {
    if (!finishingRoute?.id) {
      console.error('No finishing route available for printing.');
      return;
    }
    this.finishingRouteProxyService.printFinishingReport(finishingRoute.id).subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `FinishingReport_${finishingRoute.number}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      },
      error: (err) => {
        console.error('Error printing finishing report:', err);
      },
    });
  }

  /**
   * Downloads a transfer report PDF based on the selected option*/
  downloadTransferReport(option: TransferReportOption): void {
    this.reportProxyService.getTransferReport(option.entityId, option.entityType, option.transferType).subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `TransferReport_${this.productionCard?.productionCardNumber}_${option.transferType}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('Individual_Production_Card_Details_Success'),
          detail: this.translate.instant('Individual_Production_Card_Details_ReportDownloaded'),
          key: 'br',
        });
      },
      error: (err) => {
        console.error('Error downloading transfer report:', err);
      },
    });
  }
  //#region Change Dyeing Machine

  /* Shows the change dyeing machine button only when status is BRD or before
   */
  get canChangeDyeingMachine(): boolean {
    if (_.isNil(this.productionCard) || !this.isProductionCardsEditor) return false;
    // BRD = 7, so we allow change if status <= 7
    return this.productionCard.status <= ProductionCardStatus.Brd;
  }

  openChangeDyeingMachineModal(): void {
    const currentAsset = this.individualProductionCardService.dyeingAsset;
    const minPorts = currentAsset?.dyeingMachineDetails?.numberOfPorts ?? 0;
    const bundles = this.individualProductionCardService.productionCardBundles.filter(
      (b) => b.status !== ProductionCardBundleStatus.Removed && b.status !== ProductionCardBundleStatus.Used,
    );
    const productionCardStatus = this.productionCard?.status ?? 0;

    // Calculate total assigned lbs from bundles
    // If bundle is weighed, use actualWeightInLbs; otherwise sum the rolls' weights
    const totalAssignedLbs = bundles.reduce((total, bundle) => {
      const isWeighed = (bundle.actualWeightInLbs && bundle.actualWeightInLbs > 0) || bundle.status === ProductionCardBundleStatus.Weighed;
      if (isWeighed && bundle.actualWeightInLbs) {
        return total + bundle.actualWeightInLbs;
      }
      const bundleWeight =
        bundle.rolls?.reduce((sum, roll) => {
          const weight =
            roll.stationTimeStamps?.sort((a, b) => new Date(b.timeStamp).getTime() - new Date(a.timeStamp).getTime())[0]?.weightInLbs ??
            roll.plannedWeight ??
            0;
          return sum + weight;
        }, 0) ?? 0;
      return total + bundleWeight;
    }, 0);

    const onBundleDeleted = () => {
      this.individualProductionCardService.proxyGetProductionCardBundles();
    };

    this.modalService
      .open(
        ChangeDyeingMachineModalComponent,
        { modalClass: 'modal_middle' },
        {
          currentAsset: currentAsset,
          minNumberOfPorts: minPorts,
          bundles: bundles,
          totalAssignedLbs: totalAssignedLbs,
          productionCardStatus: productionCardStatus,
          onBundleDeleted: onBundleDeleted,
        },
      )
      .subscribe((res) => {
        if (res.action === 'save' && res.data) {
          this.changeDyeingMachine(res.data.selectedAsset.assetId);
        }
      });
  }

  private changeDyeingMachine(newAssetId: string): void {
    if (_.isNil(this.productionCard?.id)) return;

    this.isChangingDyeingMachine.set(true);
    this.individualProductionCardService.changeDyeingMachine(this.productionCard.id, newAssetId).subscribe({
      next: () => {
        this.isChangingDyeingMachine.set(false);
        const successMessage = this.translate.instant('Change_Dyeing_Machine_Success_With_Asset', { assetId: newAssetId });
        GlobalMessageUtil.success(this.translate.instant('Dyeing_Machine'), successMessage);
        // Reload bundles since API may have deleted empty bundles automatically
        this.individualProductionCardService.proxyGetProductionCardBundles();
      },
      error: (error) => {
        this.isChangingDyeingMachine.set(false);
        console.error('Failed to change dyeing machine:', error);
      },
    });
  }

  //#endregion

  //#region Close & Reassignment

  get canCloseProductionCard(): boolean {
    if (!this.productionCard || !this.canClose()) return false;
    return (
      this.productionCard.status === ProductionCardStatus.Finishing ||
      this.productionCard.status === ProductionCardStatus.FinishedGood ||
      this.productionCard.status === ProductionCardStatus.Shipped
    );
  }

  get canToggleReassignment(): boolean {
    if (!this.productionCard || !this.canClose()) return false;
    return this.productionCard.status === ProductionCardStatus.Closed || this.productionCard.status === ProductionCardStatus.Reassignment;
  }

  openCloseProductionCardModal(): void {
    if (!this.productionCard?.id) return;

    this.isClosing.set(true);
    this.productionCardProxyService.getClosePreview(this.productionCard.id).subscribe({
      next: (response) => {
        this.isClosing.set(false);
        if (!response.success || !response.data) {
          GlobalMessageUtil.error('Error', 'Failed to load close preview.');
          return;
        }

        this.modalService
          .open(
            CloseProductionCardModalComponent,
            { modalClass: 'modal_middle' },
            {
              closePreview: response.data,
              productionCardNumber: this.productionCard!.productionCardNumber,
            },
          )
          .subscribe((res) => {
            if (res.action === 'save') {
              this.executeStatusChange(ProductionCardStatus.Closed);
            }
          });
      },
      error: (error) => {
        this.isClosing.set(false);
        console.error('Failed to load close preview:', error);
      },
    });
  }

  openReassignmentModal(): void {
    if (!this.productionCard) return;

    const targetStatus =
      this.productionCard.status === ProductionCardStatus.Closed ? ProductionCardStatus.Reassignment : ProductionCardStatus.Closed;

    this.modalService
      .open(
        ReassignmentModalComponent,
        { modalClass: 'modal_small' },
        {
          productionCardNumber: this.productionCard.productionCardNumber,
          targetStatus: targetStatus,
        },
      )
      .subscribe((res) => {
        if (res.action === 'save') {
          this.executeStatusChange(targetStatus);
        }
      });
  }

  private executeStatusChange(newStatus: ProductionCardStatus): void {
    if (!this.productionCard?.id) return;

    const previousStatus = this.productionCard.status;
    this.individualProductionCardService.changeStatus(this.productionCard.id, newStatus).subscribe({
      next: (updatedCard) => {
        this.status.setValue(updatedCard.status, { emitEvent: false });
        const statusLabel = this.productionCardStatusValues[newStatus] ?? newStatus.toString();
        GlobalMessageUtil.success(
          this.translate.instant('Success'),
          this.translate.instant('Production_Card_Change_Status_Success', { status: statusLabel }),
        );
        if (previousStatus === ProductionCardStatus.ReadyForFinishing && updatedCard.status === ProductionCardStatus.Finishing) {
          this.loadFinishingRoutes();
        }
      },
      error: () => {
        this.status.setValue(this.productionCard!.status, { emitEvent: false });
      },
    });
  }

  //#endregion
}
